import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  JOB_APPLICATION_CONFIRMATION_EMAIL_PREVIEW_QUERY,
  type EmailPreview,
  type JobApplicationConfirmationEmailPreviewInput,
  type JobApplicationConfirmationEmailPreviewQueryResponse,
  type JobApplicationConfirmationEmailPreviewQueryVariables
} from "../_queries/emailPreviewQueries";

export type JobApplicationConfirmationPreviewInput = JobApplicationConfirmationEmailPreviewInput;

export function useJobApplicationConfirmationEmailPreview() {
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const [fetchPreview] = useLazyQuery<
    JobApplicationConfirmationEmailPreviewQueryResponse,
    JobApplicationConfirmationEmailPreviewQueryVariables
  >(JOB_APPLICATION_CONFIRMATION_EMAIL_PREVIEW_QUERY, { fetchPolicy: "network-only" });

  const resetPreview = useCallback(() => {
    activeRequestIdRef.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  const loadPreview = useCallback(
    async (input: JobApplicationConfirmationPreviewInput) => {
      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;

      setLoading(true);
      setPreview(null);
      setError(null);

      const result = await fetchPreview({
        variables: { input }
      });

      if (requestId !== activeRequestIdRef.current) {
        return result;
      }

      if (result.error) {
        setError(result.error);
        setPreview(null);
      } else {
        setPreview(result.data?.jobApplicationConfirmationEmailPreview ?? null);
        setError(null);
      }

      setLoading(false);
      return result;
    },
    [fetchPreview]
  );

  return {
    loadPreview,
    resetPreview,
    preview,
    loading,
    error
  };
}
